import Layout from '@/components/layout/Layout';
import { FC } from 'react';
import Link from 'next/link';
import Breadcrumbs from '@/components/ui/breadcrumbs/Breadcrumbs';
import SectionTitle from '@/components/ui/section-title/SectionTitle';
import { getAuthorFullName } from '@/helpers/helpers';
import { IAuthorDetail } from '@/interfaces/authorDetail.interface';
import { getFirstLetterCaps } from '@/helpers/helpers';
import { LITRES_AUTHORS } from '@/helpers/variables';
import styles from './AuthorDetail.module.scss';

interface IAuthorDetailProps {
  authorInfo: IAuthorDetail;
}

const AuthorDetail: FC<IAuthorDetailProps> = ({ authorInfo }) => {
  const authorFullName = getAuthorFullName(authorInfo.base_author_data.first_name, authorInfo.base_author_data.middle_name, authorInfo.base_author_data.last_name);
  const pageHref = `/author/id${authorInfo.id}`;

  const SEO_TITLE = `${authorFullName}: скачать книги в fb2 или читать онлайн бесплатно`;
  const SEO_DESCRIPTION = `Все книги автора ${authorFullName} в электронной библиотеке Fantasy Worlds. ✔️ Скачать бесплатно в формате epub, fb2, rtf, mobi, pdf для телефона, андроида, айфона, ipad. ✔️ Читать онлайн на сайте.`;
  const SEO_CANONICAL = pageHref;

  const getBookBreadcrumbs = () => {
    return {
      __html: `{
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem","position": 1,"name": "Главная","item": "https://fantasy-worlds.ru"},
        {"@type": "ListItem","position": 2,"name": "${authorFullName}","item": "https://fantasy-worlds.ru/author/id${authorInfo.id}/"}
       ]
    }`,
    };
  };

  const isLitresAuthor = LITRES_AUTHORS.some((item) => item === authorInfo.alias);

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} pageHref={pageHref}>
      <Breadcrumbs firstPageName={authorFullName} />
      <script type="application/ld+json" dangerouslySetInnerHTML={getBookBreadcrumbs()} key="product-jsonld" />

      <SectionTitle name={authorFullName} />

      <div className={styles.authorCard}>
        <div className={styles.authorCard__wrap}>
          <div className={styles.authorCard__imgWrap}>
            {/* Получить фото с бэка */}
            {/* <img className={styles.authorCard__img} src="${DEV_ABSOLUTE_URL}/covers_200/71209909.jpg1" alt="author-img" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} /> */}
          </div>
          <div className={styles.authorCard__genres}>
            {authorInfo.author_popular_genres.length > 0 && (
              <>
                <div className={styles.authorCard__genresTitle}>Жанры, в которых пишет автор:</div>
                <div className={styles.authorCard__genresList}>
                  {authorInfo.author_popular_genres.map((genre) => (
                    <Link prefetch={false} key={genre.id} className={styles.authorCard__genreLink} href={`/genre/${genre.alias}`}>
                      {getFirstLetterCaps(genre.title)}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        {isLitresAuthor && (
          <Link prefetch={false} className={styles.litresAuthorLink} href={`https://www.litres.ru/author/${authorInfo.alias}/`} target="_blank">{`${authorInfo.base_author_data.first_name} ${authorInfo.base_author_data.last_name} книги`}</Link>
        )}
      </div>

      {authorInfo.series.length > 0 && (
        <>
          <div className={styles.subTitle}>Книги:</div>
          <div className={styles.series}>
            {authorInfo.series.map((item) => (
              <div key={item.series_title} className={styles.series__item}>
                {item.series_alias ? (
                  <Link prefetch={false} href={`/series/${item.series_alias}`} className={styles.title}>
                    {item.series_title}
                  </Link>
                ) : (
                  <div className={`${styles.title} ${styles.title_black}`}>{item.series_title}</div>
                )}
                <ul className={item.books.length === 1 ? `${styles.series__list} ${styles.series__list_oneColumn}` : `${styles.series__list}`}>
                  {item.books.map((itemBook) => (
                    <li key={itemBook.id} className={styles.series__listItem}>
                      <Link prefetch={false} href={`/lib/id${itemBook.id}`} className={styles.series__link}>
                        {itemBook.title}
                      </Link>
                      <span className={styles.series__listItemType}>({itemBook.type === 'audio_book' ? 'aудиокнига' : 'книга'})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </Layout>
  );
};

export default AuthorDetail;
